import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  AddRoom,
  OpenAPI,
  Room,
  RoomOrder,
  RoomService,
  RoomStatus,
  RoomStatusCounts,
  Rooms,
  RoomsStatus,
} from "@/api";
import resolve from "@/common/resolver";

export type RoomState = {
  rooms: Rooms | null;
  companyRooms: Rooms | null;
  selectedRoom: Room | null;
  roomsStatus: RoomsStatus[];
  roomStatusCount: RoomStatusCounts;
};

const state: RoomState = {
  rooms: null,
  companyRooms: null,
  selectedRoom: null,
  roomsStatus: [],
  roomStatusCount: [],
};

const getters: GetterTree<RoomState, RootState> = {
  rooms: state => state.rooms,
  selectedRoom: state => state.selectedRoom,
  companyRooms: state => state.companyRooms,
  roomsStatus: state => state.roomsStatus,
  roomStatusCount: state => state.roomStatusCount,
};

const mutations: MutationTree<RoomState> = {
  SET_ROOMS: (state, rooms) => {
    state.rooms = rooms;
  },
  SET_COMPANYROOMS: (state, rooms) => {
    state.companyRooms = rooms;
  },
  ADD_ROOM: (state, room) => {
    if (state.rooms == null) {
      state.rooms = [];
    }
    state.rooms.push(room);
  },
  REMOVE_ROOM: (state, room) => {
    let index = state.rooms?.findIndex((item: Room) => item.id == room.id);
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.rooms?.splice(index, 1);
    }
  },
  UPDATE_ROOM: (state, room) => {
    let index = state.rooms?.findIndex((item: Room) => item.id == room.id);
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.rooms?.splice(index, 1);
      state.rooms?.push(room);
    }
  },
  SET_SELECTED_ROOM: (state, roomId) => {
    if (roomId && state.rooms?.length) {
      const room = state.rooms.find(room => room.id == roomId);
      state.selectedRoom = room ? room : null;
    } else state.selectedRoom = null;
  },
  SET_ROOMS_STATUS: (state, roomsStatus) => {
    state.roomsStatus = roomsStatus;
  },
  SET_ROOMS_STATUS_COUNT: (state, roomStatusCount) => {
    state.roomStatusCount = roomStatusCount;
  },
};

const actions: ActionTree<RoomState, RootState> = {
  fetchFloorRooms: async ({ commit, rootState }, floorId: number) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const rooms = await RoomService.getFloorRooms({ companyId, floorId });
      commit("SET_ROOMS", rooms);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchCompanyRooms: async ({ commit, rootState, getters }, force = false) => {
    const hasRooms = getters["companyRooms"]?.length > 0;
    if (hasRooms && !force) {
      return;
    }
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const rooms = await RoomService.getCompanyRooms({ companyId });
      commit("SET_COMPANYROOMS", rooms);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addFloorRoom: async (
    { commit, rootState, dispatch },
    { floorId, requestBody }: { floorId: number; requestBody: AddRoom },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const room = await RoomService.addFloorRoom({
        companyId,
        floorId,
        requestBody,
      });
      commit("ADD_ROOM", room);
      await dispatch("fetchFloorRooms", floorId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateFloorRoom: async (
    { commit, rootState },
    { floorId, requestBody }: { floorId: number; requestBody: Room },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const room = await RoomService.updateFloorRoom({
        companyId,
        floorId,
        requestBody,
      });
      commit("UPDATE_ROOM", room);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  removeFloorRoom: async (
    { commit, rootState },
    { floorId, requestBody }: { floorId: number; requestBody: Room },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      await RoomService.deleteFloorRoom({
        companyId,
        floorId,
        requestBody,
      });
      commit("REMOVE_ROOM", requestBody);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  setSelectedRoom: ({ commit }, roomId: number) => {
    commit("SET_SELECTED_ROOM", roomId);
  },
  fetchRoomsStatus: async ({ commit, rootState }, date?: string) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const roomsStatus = await RoomService.getCompanyRoomsStatus({
        companyId,
        date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      commit("SET_ROOMS_STATUS", roomsStatus);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchRoomsStatusCount: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const roomStatusCount = await RoomService.getCompanyRoomsStatusCount({
        companyId,
      });
      commit("SET_ROOMS_STATUS_COUNT", roomStatusCount);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addRoomStatus: async (
    { commit, rootState, dispatch },
    requestBody: RoomStatus,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      await RoomService.addCompanyRoomStatus({
        companyId,
        requestBody,
      });
      dispatch("fetchRoomsStatus");
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateRoomStatus: async (
    { commit, rootState, dispatch },
    { requestBody, document }: { requestBody: RoomStatus; document?: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    const formData = new FormData();
    if (document) formData.append("file", document, document?.name);
    formData.append("requestBody", JSON.stringify(requestBody));
    try {
      const res = await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/room/status`,
        {
          method: "PUT",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      )
        .then(res => res.json())
        .catch(e => {
          if (e instanceof Error) {
            commit("alert/SET_SHOW_ERROR", e.message, { root: true });
          }
        });
      if (res && res.statusText && res.statusText !== "OK") {
        throw new Error(res.message || "Failed to update room status");
      } else {
        dispatch("fetchRoomsStatus");
      }
    } catch (e) {
      if (e instanceof Error) {
        commit("alert/SET_SHOW_ERROR", e.message, { root: true });
      }
    }
  },
  orderRooms: async (
    { commit, rootState },
    { floorId, requestBody }: { floorId: number; requestBody: RoomOrder },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      commit("SET_ROOMS", requestBody.rooms);
      await RoomService.sortCompanyFloorRooms({
        companyId,
        requestBody,
        floorId,
      });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const room: Module<RoomState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
